import { useState } from "react";
import {
  Box,
  TextField,
  Button as MuiButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import apiCall from "../../../../utils/apiCall";
import DeactivateApplicantByEmail from "./DeactivateApplicant";

interface DeactivateApplicant {
  ApplicantId: number;
  FirstName: string;
  LastName: string;
  ApplicationDate: string;
  Gender: string;
  MaritalStatus: string;
  Email: string;
}

interface ComponentProps {
  handleClose: () => void;
}

const SearchApplicantToDeactivate: React.FC<ComponentProps> = ({ handleClose }) => {
  const [email, setEmail] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foundApplicant, setFoundApplicant] = useState<DeactivateApplicant | null>(null);

  const handleSearch = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter an applicant email");
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const res = await apiCall().get("/Applicant/GetAllApplicant", {
        params: { searchTerm: trimmedEmail },
      });
      const data = res?.data?.Data;
      const applicants: DeactivateApplicant[] = Array.isArray(data) ? data : [];
      const match = applicants.find(
        (applicant) => applicant.Email?.toLowerCase() === trimmedEmail.toLowerCase()
      );

      if (match) {
        setFoundApplicant(match);
      } else {
        setError("No applicant found with this email");
      }
    } catch (err: any) {
      setError(err?.response?.data?.title || err.message || "Failed to search applicant");
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  if (foundApplicant) {
    return (
      <DeactivateApplicantByEmail
        DeactivateApplicantDetails={foundApplicant}
        handleClose={handleClose}
      />
    );
  }

  return (
    <Container>
      <Typography variant="h6" gutterBottom>
        Search Applicant by Email
      </Typography>
      <Box display="flex" gap={2} alignItems="flex-start">
        <TextField
          fullWidth
          size="small"
          placeholder="Enter applicant email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          error={Boolean(error)}
          helperText={error}
          autoFocus
        />
        <MuiButton
          variant="contained"
          color="success"
          onClick={handleSearch}
          disabled={isSearching}
        >
          {isSearching ? <CircularProgress size={20} color="inherit" /> : "Search"}
        </MuiButton>
      </Box>
    </Container>
  );
};

export default SearchApplicantToDeactivate;

const Container = styled("div")`
  margin: 2rem;
`;
